/**
 * Compact pill-shaped toggle for the dispatch header. Surfaces the master
 * on/off switch for the background auto-scheduler runner plus a "fresh
 * as of …" timestamp so dispatchers know how recently the algorithm
 * touched the schedule.
 *
 * Design constraints from product:
 *   • Must read at-a-glance from across the room (color + label).
 *   • Switching state must feel instant — optimistic flip, then sync.
 *   • Hover reveals what changed in the last run + a "manual run" hint.
 */
import { Loader2, Power, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface AutoScheduleSettingsSummary {
    startedAt?: string;
    durationMs?: number;
    proposalsApplied?: number;
    jobsConsidered?: number;
    jobsProposed?: number;
    jobsSkipped?: number;
    error?: string;
}

interface AutoScheduleToggleProps {
    enabled: boolean;
    loading: boolean;
    toggling: boolean;
    lastRunAt: string | null;
    intervalMinutes: number;
    lastRunSummary?: AutoScheduleSettingsSummary;
    onToggle: (next: boolean) => void;
}

function formatRelative(iso: string | null): string {
    if (!iso) return "never";
    const ms = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(ms) || ms < 0) return "moments ago";
    const min = Math.floor(ms / 60_000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    return `${Math.floor(hr / 24)} d ago`;
}

export function AutoScheduleToggle({
    enabled,
    loading,
    toggling,
    lastRunAt,
    intervalMinutes,
    lastRunSummary,
    onToggle,
}: AutoScheduleToggleProps) {
    const tooltipParts: string[] = [];
    if (enabled) {
        tooltipParts.push(`Runs every ${intervalMinutes} min`);
        if (lastRunAt) tooltipParts.push(`Last run ${formatRelative(lastRunAt)}`);
        if (lastRunSummary?.proposalsApplied !== undefined) {
            tooltipParts.push(
                `Last pass placed ${lastRunSummary.proposalsApplied} stop${lastRunSummary.proposalsApplied === 1 ? "" : "s"}`,
            );
        }
        if (lastRunSummary?.error) {
            tooltipParts.push(`Last run failed: ${lastRunSummary.error}`);
        }
    } else {
        tooltipParts.push("Auto-scheduling is OFF — drag jobs onto techs manually");
    }

    return (
        <Button
            variant={enabled ? "default" : "outline"}
            size="sm"
            disabled={loading || toggling}
            onClick={() => onToggle(!enabled)}
            title={tooltipParts.join(" · ")}
            className={cn(
                "h-9 gap-1.5 px-3",
                enabled
                    ? "bg-emerald-700 text-white shadow-sm hover:bg-emerald-800 dark:bg-emerald-700 dark:hover:bg-emerald-800"
                    : // G6.1 polish: the toggle lives on the dark command bar. The
                      // outline variant sets bg-card but NO text color, so the OFF
                      // label inherited the bar's white shell-foreground and went
                      // white-on-white in the light theme. Pin dark bg + light
                      // text explicitly (theme-independent, matches DARK_CTRL).
                      "border-shell-foreground/25 bg-shell-foreground/10 text-shell-foreground hover:bg-shell-foreground/20 hover:text-shell-foreground",
            )}
        >
            {toggling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
            ) : enabled ? (
                <Zap className="h-4 w-4 fill-white" />
            ) : (
                <Power className="h-4 w-4" />
            )}
            <span className="flex flex-col items-start leading-tight">
                <span className="text-xs font-semibold">Auto-schedule {enabled ? "ON" : "OFF"}</span>
                {enabled && (
                    <span className={cn("text-[10px] font-normal", "opacity-90")}>
                        Last run {formatRelative(lastRunAt)}
                    </span>
                )}
            </span>
        </Button>
    );
}
